/**
 * /pages/Payments.js – календарь платежей по накладным
 * /stores/payments.js – бизнес-логика календаря платежей
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';

import pageLoader from '../images/loader_big.svg';

import { PaymentsState } from '../stores/payments';
import { InvoiceApi } from '../stores/apiMethods';

import { FormModal } from '../shared/FeedbackComponents';
import { ButtonSelectGroup } from '../shared/FormElements';
import { PayButton } from '../shared/PageComponents';

import { formatDate, getRubs } from '../functions';
import { DateUtils } from '../dictionary';
import { confirmState, errorState } from '../stores/common';

export class PaymentsPage extends Component {
	constructor(props) {
		super(props);
		this.store = new PaymentsState();
		this.getData();
	}

	/** API запрос на получение платежей за выбранный месяц */
	getData = () => {
		this.store.IsSendRequest = false;
		this.store.setProvider();
		InvoiceApi.getCalendar(this.store.CurrentMonth, (data) => {
			this.store.setMonth(data.Success ? data.Dates : []);
		});
	}

	/** Изменение активного месяца */
	changeMonth = (value) => {
		this.store.CurrentMonth = value;
		this.getData();
	}

	render() {
		return(
			<div className="page" id="PaymentPage">
				<div className="head">
					<h2>Календарь платежей</h2>
					<ButtonSelectGroup
						Id="CurrentMonth"
						RowClass="monthsFilter"
						List={this.store.Months}
						Value={this.store.CurrentMonth}
						onChange={this.changeMonth}
					/>
				</div>
				{!this.store.IsSendRequest &&
					<section><div className="loader"><img src={pageLoader} alt="logo" /></div></section>
				}
				{this.store.IsSendRequest &&
					<section className="table" style={{ "--weeks-count": this.store.Weeks.length }}>
						<div className="weekday"></div>
						{_.map(DateUtils.getFullWeek(true), (day) =>
							<div className="weekday" key={"wd" + day.Id}>{day.Name}</div>
						)}
						<div className="weekday"></div>
						{_.map(this.store.Weeks, (week, index) =>
							<React.Fragment key={"w" + index}>
								<span className="weekTitle">{week.Title}</span>
								{_.map(week.Days, (day) =>
									<DayBlock Day={day} key={day.Date} store={this.store} />
								)}
								<WeekSummaryBlock Week={week} />
							</React.Fragment>
						)}
					</section>
				}
				{this.store.CurrentProvider &&
					<ProviderPopup Provider={this.store.CurrentProvider} CanPay={this.store.CanPay} onClose={this.getData} />
				}
			</div>
		)
	}
}
PaymentsPage = observer(PaymentsPage);

class DayBlock extends Component {
	render() {
		const day = this.props.Day;
		return(
			<div className={"day" + (day.Disabled ? " disabled" : "") + (day.Today ? " today" : "")}>
				{!day.Disabled && <h5>{formatDate(day.Date)}</h5>}
				{_.map(day.Providers, (provider) => {
					const statusName = provider.Status === 3 ? "pay" : provider.Alert ? "alert" : "notPay";
					return(
						<button type="button" className={"provider " + statusName} key={provider.Provider}
							onClick={() => this.props.store.setProvider(day.Date, provider.Provider)}>
							<span title={provider.Provider}>{provider.Provider}</span>
							<span className='amount'>{getRubs(provider.Total, true, true)}</span>
							{provider.PaymentIcon}
						</button>
					)
				})}
			</div>
		)
	}
}

class WeekSummaryBlock extends Component {
	render() {
		return(
			<div className="sum">
				{_.map(this.props.Week.Amounts, (type) =>
					<PaymentTypeSummaryBlock Type={type} key={"wa" + type.Type} />
				)}
			</div>
		)
	}
}

const PaymentTypeSummaryBlock = ({ Type, ShowName, ShowVAT }) =>
	<p className={"paymentTypeSummary " + Type.StatusClass} title={Type.Name}>
		<span className="paymentTypeTitle">
			{Type.Icon}
			{ShowName && Type.Name}
		</span>
		<span className="total amount" title="Не оплачено">
			{getRubs(Type.Total, true, true, undefined, undefined, undefined, true)}
			{ShowVAT && <small>в т.ч. НДС {getRubs(Type.TotalVAT, true, true, undefined, undefined, undefined, true)}</small>}
		</span>
		<span className="payment amount" title="Оплачено">
			{getRubs(Type.Payment, true, true, undefined, undefined, undefined, true)}
			{ShowVAT && <small>в т.ч. НДС {getRubs(Type.PaymentVAT, true, true, undefined, undefined, undefined, true)}</small>}
		</span>
	</p>;

class ProviderPopup extends Component {
	/** Открытие подтверждения оплаты накладной 
	 * @param {number} id выбранной накладной
	*/
	handlePay = (id) => {
		const provider = this.props.Provider,
			invoice = _.find(provider.Invoices, { Id: id }),
			text = <span>Вы действительно хотите оплатить накладную&nbsp;
				<b key="b1">«{invoice ? invoice.Name : ""}»</b>?</span>;

		confirmState.openConfirm(text, () => {
			InvoiceApi.pay(id, (data) => {
				confirmState.closeConfirm();
				if (data.Success) provider.setPaid(id);
				else errorState.setError(data.ErrorCode);
			});
			return false;
		});
	}

	render() {
		const provider = this.props.Provider,
			title = <span className="providerModalTitle">
				<span>{provider.Provider}</span>
				<span>{formatDate(provider.Date)}</span>
			</span>;

		return(
			<FormModal 
				ClassName="providerModal" 
				CancelTitle="Закрыть" 
				Title={title}
				onClose={this.props.onClose}
			>
				{_.map(provider.PaymentGroups, (group) =>
					<div className="type" key={"p" + group.Type}>
						<PaymentTypeSummaryBlock Type={group} ShowName ShowVAT />
						<PaymentInvoicesBlock Invoices={group.Invoices} Date={provider.Date}
							CanPay={this.props.CanPay} onPay={this.handlePay} />
					</div>
				)}
			</FormModal>
		)
	}
}
ProviderPopup = observer(ProviderPopup);

export const PaymentInvoicesBlock = ({ Invoices, CanPay, Date, onPay }) =>
	<div className="paymentInvoices">
		{_.map(Invoices, (payment) =>
			<div className="invoice" key={payment.Id}>
				<span>{payment.Name} от {formatDate(Date)}</span>
				<span>
					Сумма: {getRubs(payment.Amount, true, true, " ", true, undefined, true)},&nbsp;
					НДС: {getRubs(payment.VATAmount, true, true, " ", true, undefined, true)}
				</span>
				{payment.Status !== 3 && CanPay ?
					<PayButton Deadline={payment.DeadlinePayment}
						Expired={payment.Expired} onClick={() => onPay(payment.Id)} />
					: <span className="comment">Оплачено {formatDate(payment.PaymentDate)} {payment.PaymentUser}</span>
				}
			</div>
		)}
	</div>;
